import _ from "lodash";
import React, { useEffect, useLayoutEffect, memo, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { connect } from "react-redux";
import { Button, Tooltip } from "@toddle-edu/ds-web";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";

import { useI18n } from "Hooks";
import { compose } from "lib/apolloClient";
import { formatDate } from "Utils/DateTimeHelpers";
import {
  getIsAIDebugModeEnabled,
  scrollToBottomInChat,
  isSameDay,
} from "MessagingV2/modules/Utils";
import { useChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import {
  cancelSummaryGeneration,
  getDebugPropsForSummaryMessage,
  getMessagingV2ReduxState,
  removeDebugPropsForSummaryMessage,
} from "MessagingV2/modules/MessagingModule";
import { InformationOutlined } from "@toddle-edu/ds-icons";
import classes from "./SummaryMessage.scss";

const styles = {
  aiStripContainerStyle: {
    borderRadius: "0px 0px 8px 8px",
  },
  overlayTooltipStyles: {
    maxWidth: "280px",
  },
};

const FORMATS = {
  START: "D MMM",
  END: "D MMM YYYY",
};
const SummaryMessage = props => {
  const { isGeneratingSummary, message } = props;
  const { id: messageId, label, summaryRange } = message;
  const { start, end } = summaryRange || {};

  const { t } = useI18n(["common"]);
  const { chatId } = useChatOptions();

  // TODO: remove the following logic once we start saving summary messages on backend
  useEffect(
    () => () => {
      // Note: As of now we are not saving summary messages on backend
      // so they get removed from redux state when component unmounts
      // thus removing debug props for summary message.
      removeDebugPropsForSummaryMessage(messageId);
    },
    [messageId]
  );

  useLayoutEffect(() => {
    if (!isGeneratingSummary) return;

    // we are generating summary and label is changed, so scroll to bottom
    scrollToBottomInChat({ chatId, scrollInNextTick: true });
  }, [isGeneratingSummary, label, chatId]);

  const onClickStop = () => cancelSummaryGeneration(chatId);

  const aiDebugModeObj = getDebugPropsForSummaryMessage(messageId);

  const title = isGeneratingSummary
    ? t("common:summarising")
    : t("common:summary");

  const formattedDateSuffix = useMemo(() => {
    if (!start || !end) return "";

    const formattedDate = isSameDay(start, end)
      ? formatDate({ date: end, format: FORMATS.END })
      : `${formatDate({
          date: start,
          format: FORMATS.START,
        })} - ${formatDate({
          date: end,
          format: FORMATS.END,
        })}`;
    return `(${formattedDate})`;
  }, [start, end]);

  return (
    <div className={classes.container}>
      <div className={classes.innerContainer}>
        <div className={classes.upperRow}>
          <div className={classes.titleAndLoader}>
            <div
              className={classNames(
                "text-textSubtle",
                "text-label-3",
                classes.title
              )}
            >
              {`${title} ${formattedDateSuffix}`}
            </div>
            {isGeneratingSummary ? (
              <span className={classes.animatedDotLoading} />
            ) : (
              <Tooltip
                tooltip={t("common:summary_only_visible_to_you")}
                overlayInnerStyle={styles.overlayTooltipStyles}
              >
                <InformationOutlined size="xxx-small" variant="subtle" />
              </Tooltip>
            )}
          </div>
          {isGeneratingSummary ? (
            <Button size="small" variant="inline" onClick={onClickStop}>
              {t("common:stop")}
            </Button>
          ) : null}
        </div>

        <div
          className={classNames(
            "text-textDefault",
            "text-body-2",
            classes.summary
          )}
        >
          {label}
        </div>
      </div>

      {!isGeneratingSummary &&
      getIsAIDebugModeEnabled() &&
      !_.isEmpty(aiDebugModeObj) ? (
        <AIFeedbackStrip
          {...aiDebugModeObj}
          containerStyle={styles.aiStripContainerStyle}
        />
      ) : null}
    </div>
  );
};

SummaryMessage.propTypes = {
  message: PropTypes.object.isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const messageId = _.get(ownProps, "message.id");
  const isGeneratingSummary = !!_.get(getMessagingV2ReduxState(state), [
    "summaryGenerationStatus",
    messageId,
  ]);

  return { isGeneratingSummary };
};

export default compose(connect(mapStateToProps), memo)(SummaryMessage);
